import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Grid,
  ThemeProvider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const TemplateForm = ({ open, onClose, onSubmit, template, theme }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    body: "",
  });
  const [errors, setErrors] = useState({});

  const placeholders = [
    {
      name: t("templateForm.firstNamePlaceholder"),
      description: t("templateForm.candidateFirstName"),
    },
    {
      name: t("templateForm.lastNamePlaceholder"),
      description: t("templateForm.candidateLastName"),
    },
    {
      name: t("templateForm.namePlaceholder"),
      description: t("templateForm.candidateFullName"),
    },
    {
      name: t("templateForm.companyPlaceholder"),
      description: t("templateForm.yourCompanyName"),
    },
    {
      name: t("templateForm.positionPlaceholder"),
      description: t("templateForm.jobPosition"),
    },
    {
      name: t("templateForm.datePlaceholder"),
      description: t("templateForm.interviewDate"),
    },
    {
      name: t("templateForm.timePlaceholder"),
      description: t("templateForm.interviewTime"),
    },
    {
      name: t("templateForm.locationPlaceholder"),
      description: t("templateForm.interviewLocation"),
    },
    {
      name: t("templateForm.subjectPlaceholderName"),
      description: t("templateForm.subjectPlaceholder"),
    },
  ];

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        subject: template.subject || "",
        body: template.body || "",
      });
    } else {
      setFormData({
        name: "",
        subject: "",
        body: "",
      });
    }
    setErrors({});
  }, [template, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textField = document.getElementById("body-field");
    const cursorPosition = textField?.selectionStart || formData.body.length;
    const textBefore = formData.body.substring(0, cursorPosition);
    const textAfter = formData.body.substring(cursorPosition);

    const newBody = `${textBefore}{${placeholder}}${textAfter}`;
    setFormData((prev) => ({ ...prev, body: newBody }));

    // Special handling for Subject/Sujet placeholders - auto-fill the subject field
    if (placeholder === "Subject" || placeholder === "Sujet") {
      const subjectTemplate = t("board.interview_invitation_subject", {
        company: "{" + t("templateForm.companyPlaceholder") + "}",
        position: "{" + t("templateForm.positionPlaceholder") + "}",
        interviewDate: "{" + t("templateForm.datePlaceholder") + "}",
      });
      setFormData((prev) => ({ ...prev, subject: subjectTemplate }));
    }

    setTimeout(() => {
      if (textField) {
        textField.focus();
        const newPosition = cursorPosition + placeholder.length + 2;
        textField.setSelectionRange(newPosition, newPosition);
      }
    }, 10);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim())
      newErrors.name = t("templateForm.templateNameRequired");
    if (!formData.subject.trim())
      newErrors.subject = t("templateForm.subjectRequired");
    if (!formData.body.trim()) newErrors.body = t("templateForm.bodyRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 8px 32px rgba(0, 23, 231, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2.5,
            px: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {template
              ? t("templateForm.editEmailTemplate")
              : t("templateForm.createNewEmailTemplate")}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ p: 1 }}>
            <TextField
              label={t("templateForm.templateName")}
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              margin="normal"
              variant="outlined"
              error={!!errors.name}
              helperText={errors.name}
              autoFocus
            />

            <TextField
              label={t("templateForm.emailSubject")}
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              fullWidth
              margin="normal"
              variant="outlined"
              error={!!errors.subject}
              helperText={errors.subject}
            />

            <TextField
              label={t("templateForm.emailBody")}
              name="body"
              id="body-field"
              value={formData.body}
              onChange={handleChange}
              fullWidth
              multiline
              rows={10}
              margin="normal"
              variant="outlined"
              error={!!errors.body}
              helperText={errors.body}
            />

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
            >
              {t("templateForm.availablePlaceholders")}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {t("templateForm.clickPlaceholderToInsert")}
            </Typography>

            <Grid container spacing={1} sx={{ mt: 1 }}>
              {placeholders.map((placeholder) => (
                <Grid item key={placeholder.name}>
                  <Chip
                    label={`{${placeholder.name}}`}
                    onClick={() => insertPlaceholder(placeholder.name)}
                    clickable
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t("templateForm.placeholderGuide")}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {placeholders.map((placeholder) => (
                  <Box key={placeholder.name} sx={{ display: "flex", gap: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", minWidth: "120px" }}
                    >
                      {`{${placeholder.name}}`}
                    </Typography>
                    <Typography variant="body2">
                      {placeholder.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: "space-between", gap: 2 }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              borderColor: "#0017E7",
              color: "#0017E7",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#0010a3",
                backgroundColor: "rgba(0, 23, 231, 0.04)",
              },
            }}
          >
            {t("templateForm.cancel")}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.2,
              fontWeight: 600,
              background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
              boxShadow: "0px 4px 12px rgba(0, 23, 231, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                boxShadow: "0px 6px 16px rgba(0, 23, 231, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {template
              ? t("templateForm.updateTemplate")
              : t("templateForm.saveTemplate")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default TemplateForm;
